import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import WizardLayout from '@/components/wizard/WizardLayout';
import { toast } from 'sonner';

const WizardConverter = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [jobId, setJobId] = useState('');
  const [isConverting, setIsConverting] = useState(true);
  const [ixbrlData, setIxbrlData] = useState([]);
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    const converterData = JSON.parse(sessionStorage.getItem('ixbrl_converter') || '{}');
    if (!converterData.jobId) {
      toast.error('Session expired');
      navigate('/jobs');
      return;
    }
    setCompany(converterData.company);
    setJobId(converterData.jobId);

    setTimeout(() => {
      const mockData = [
        { id: 1, line: 'Revenue', value: '1,250,000', tag: 'frs102:Revenue' },
        { id: 2, line: 'Cost of Sales', value: '750,000', tag: 'frs102:CostOfSales' },
        { id: 3, line: 'Gross Profit', value: '500,000', tag: 'frs102:GrossProfit' },
        { id: 4, line: 'Operating Expenses', value: '200,000', tag: 'frs102:AdministrativeExpenses' },
        { id: 5, line: 'Net Income', value: '300,000', tag: 'frs102:ProfitLoss' }
      ];
      setIxbrlData(mockData);
      setIsConverting(false);
      toast.success('Conversion completed');
    }, 3000);
  }, [navigate]);

  const handleDownload = () => {
    const converterData = JSON.parse(sessionStorage.getItem('ixbrl_converter') || '{}');
    converterData.ixbrlData = ixbrlData;
    sessionStorage.setItem('ixbrl_converter', JSON.stringify(converterData));
    navigate('/wizard/completion');
  };

  if (!company) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="border-b border-border bg-card px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">iXBRL Converter</h1>
          <p className="text-sm text-muted-foreground">Job ID: {jobId}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setZoom(Math.max(50, zoom - 10))}>
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm font-mono w-12 text-center">{zoom}%</span>
            <Button variant="outline" size="sm" onClick={() => setZoom(Math.min(200, zoom + 10))}>
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
          <Button onClick={handleDownload} disabled={isConverting}>
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>
      </div>

      <div className="flex-1 flex">
        <div className="flex-1 border-r border-border p-8 overflow-auto" style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left', width: `${10000 / zoom}%` }}>
          <div className="bg-white text-black p-12 border border-gray-300 max-w-3xl">
            <h2 className="text-2xl font-bold mb-6">Financial Statement - FY2024</h2>
            <table className="w-full">
              <tbody>
                <tr className="border-b"><td className="py-2">Revenue</td><td className="text-right">£1,250,000</td></tr>
                <tr className="border-b"><td className="py-2">Cost of Sales</td><td className="text-right">£750,000</td></tr>
                <tr className="border-b font-semibold"><td className="py-2">Gross Profit</td><td className="text-right">£500,000</td></tr>
                <tr className="border-b"><td className="py-2">Operating Expenses</td><td className="text-right">£200,000</td></tr>
                <tr className="border-t-2 font-bold"><td className="py-2">Net Income</td><td className="text-right">£300,000</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex-1 p-8 overflow-auto">
          {isConverting ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-sm text-muted-foreground">AI is analyzing your document...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="font-semibold mb-4">iXBRL Tagged Data</h3>
              {ixbrlData.map((item) => (
                <div key={item.id} className="border border-border rounded-lg p-4 hover:bg-accent">
                  <div className="flex justify-between mb-2">
                    <p className="font-medium">{item.line}</p>
                    <p className="font-mono text-lg">{item.value}</p>
                  </div>
                  <div className="text-xs">
                    <code className="bg-muted px-2 py-1 rounded">{item.tag}</code>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WizardConverter;